import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Check, MessageSquare, Smartphone, Zap,
  ShieldCheck, Rocket, Minus, Plus, ChevronDown,
  Instagram, Facebook, Twitter, Mail, HelpCircle,
  BarChart3, Layers, Truck, Globe, X, Menu
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const faqs = [
    {
      q: "A IA responde por mim?",
      a: "Sim! Nossa IA é treinada para entender o contexto das perguntas, mostrar produtos do seu catálogo, calcular frete e até gerar o link de pagamento, tudo de forma autônoma."
    },
    {
      q: "Tem taxa por venda?",
      a: "Não. Cobramos apenas a mensalidade fixa do plano escolhido. Todo o lucro das suas vendas é 100% seu."
    },
    {
      q: "Posso usar domínio próprio?",
      a: "Com certeza. No plano Intermediário e Pro, você pode configurar seu próprio domínio (.com.br, .com) para a sua vitrine digital."
    },
    {
      q: "Preciso baixar algum aplicativo?",
      a: "Para vender, você usa apenas o nosso painel web. Para o seu cliente, ele não precisa baixar nada, tudo acontece dentro do WhatsApp que ele já usa."
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-surface-900 selection:bg-emerald-100 selection:text-emerald-900">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full z-[100] bg-white/80 backdrop-blur-md border-b border-surface-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/logozapshopsaas.png" alt="ZapShop" className="h-10 w-auto object-contain" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#solucao" className="text-sm font-semibold text-surface-500 hover:text-brand-600 transition-colors">Como funciona</a>
            <a href="#comparativo" className="text-sm font-semibold text-surface-500 hover:text-brand-600 transition-colors">Funcionalidades</a>
            <a href="#precos" className="text-sm font-semibold text-surface-500 hover:text-brand-600 transition-colors">Preços</a>
            <button
              onClick={() => navigate('/login')}
              className="bg-surface-900 text-white px-6 py-2.5 rounded-full font-bold text-xs uppercase tracking-widest hover:bg-black transition-all active:scale-95"
            >
              Acesso do Lojista
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-surface-900 p-2 focus:outline-none"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-surface-100 absolute top-20 left-0 w-full shadow-lg animate-fade-in overflow-hidden z-[90]">
            <div className="flex flex-col px-6 py-4 gap-2">
              <a href="#solucao" onClick={() => setMobileMenuOpen(false)} className="text-base font-semibold text-surface-900 hover:text-brand-600 transition-colors py-3 border-b border-surface-50">Como funciona</a>
              <a href="#comparativo" onClick={() => setMobileMenuOpen(false)} className="text-base font-semibold text-surface-900 hover:text-brand-600 transition-colors py-3 border-b border-surface-50">Funcionalidades</a>
              <a href="#precos" onClick={() => setMobileMenuOpen(false)} className="text-base font-semibold text-surface-900 hover:text-brand-600 transition-colors py-3 border-b border-surface-50">Preços</a>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/login');
                }}
                className="bg-surface-900 text-white px-6 py-4 mt-4 mb-2 rounded-xl font-bold text-sm uppercase tracking-widest hover:bg-black transition-all active:scale-95 w-full text-center"
              >
                Acesso do Lojista
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-32 md:pb-32 overflow-hidden relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none opacity-20">
          <div className="absolute top-20 right-0 w-96 h-96 bg-brand-400 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-400 rounded-full blur-[120px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="flex-1 text-center lg:text-left space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                🟢 A 1ª plataforma híbrida de venda conversacional do Brasil
              </div>

              <h1 className="text-4xl md:text-[4rem] font-black tracking-tight leading-[1.1] md:leading-[1.05] text-surface-900">
                Transforme seu WhatsApp em um <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 via-cyan-500 to-emerald-500">
                  E-commerce Completo
                </span> com IA.
              </h1>

              <p className="text-lg md:text-xl text-surface-500 font-medium leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Mais de 90% dos brasileiros preferem comprar pelo WhatsApp. Dê a eles o que querem: uma IA que atende, vende e gerencia pedidos direto no chat. Sem fricção, sem cadastros chatos.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => window.location.href = 'https://zapshop.app/register'}
                  className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-2xl shadow-emerald-500/20 transition-all active:scale-95 flex items-center justify-center gap-3"
                >
                  Criar Minha Loja no WhatsApp <ArrowRight size={20} />
                </button>
              </div>
              <p className="text-xs font-black text-emerald-600 uppercase tracking-[0.2em] animate-pulse">
                🎁 Comece agora: 07 dias de teste grátis!
              </p>

              <div className="flex items-center justify-center lg:justify-start gap-6 pt-4">
                <div className="flex items-center gap-2 text-[10px] font-bold text-surface-400 uppercase tracking-widest">
                  <ShieldCheck size={16} className="text-emerald-500" /> 🔒 Compra Segura via Mercado Pago
                </div>
                <div className="flex items-center gap-2 text-[10px] font-bold text-surface-400 uppercase tracking-widest">
                  <Zap size={16} className="text-amber-500" /> ⚡ Setup em minutos
                </div>
              </div>
            </div>

            <div className="flex-1 w-full max-w-xl animate-fade-in-right relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-500/10 to-emerald-500/10 rounded-[3rem] -rotate-6 scale-105"></div>
              <img
                src="/zapshop_hero_mockup.png"
                alt="ZapShop Mockup"
                className="w-full h-auto relative z-10 rounded-[2.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] bg-white p-2"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem vs Solution Section */}
      <section id="solucao" className="py-24 bg-surface-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-16 text-surface-900">
            Esqueça os carrinhos abandonados <br /> de sites complexos.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <MessageSquare size={32} className="text-brand-600" />,
                title: "Tudo Acontece no Zap",
                desc: "Busca de produtos, histórico e status de pedidos com mensagens rápidas e intuitivas."
              },
              {
                icon: <Smartphone size={32} className="text-cyan-500" />,
                title: "Sua Melhor Vendedora",
                desc: "IA com personalidade própria, que entende o que o cliente quer e envia fotos e links na hora."
              },
              {
                icon: <Globe size={32} className="text-emerald-500" />,
                title: "Plataforma Híbrida 2 em 1",
                desc: "Vitrine web gratuita e sincronizada com o WhatsApp. Sem custo extra de domínio ou servidor."
              }
            ].map((card, i) => (
              <div key={i} className="bg-white p-10 rounded-[2rem] shadow-soft border border-surface-100 text-left space-y-4 hover:shadow-xl transition-all group">
                <div className="w-16 h-16 rounded-2xl bg-surface-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                  {card.icon}
                </div>
                <h3 className="text-xl font-black text-surface-900 leading-tight pt-2">{card.title}</h3>
                <p className="text-surface-500 font-medium leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparative Table Section */}
      <section id="comparativo" className="py-24 overflow-hidden">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-surface-900">
              Pare de pagar por dezenas <br /> de aplicativos diferentes.
            </h2>
            <p className="text-lg text-surface-500 font-medium leading-relaxed max-w-2xl mx-auto">
              O ZapShop centraliza tudo o que você precisa para vender escala, sem custos ocultos.
            </p>
          </div>

          <div className="bg-white rounded-[2.5rem] shadow-2xl border border-surface-100 overflow-hidden">
            <div className="grid grid-cols-3 border-b border-surface-50">
              <div className="p-6 md:p-8 bg-surface-50 border-r border-surface-100"></div>
              <div className="p-6 md:p-8 text-center font-black text-xs uppercase tracking-widest text-surface-400 border-r border-surface-100">Plataformas Padrão</div>
              <div className="p-6 md:p-8 text-center bg-brand-50/30">
                <img src="/logozapshopsaas.png" alt="ZapShop" className="h-8 md:h-10 mx-auto object-contain" />
              </div>
            </div>

            {[
              { label: "Taxa por venda", they: "1 a 2%", us: "ZERO" },
              { label: "IA de Atendimento", they: "App Pago (Extra)", us: "Inclusa" },
              { label: "Automação WhatsApp", they: "R$ 200+/mês", us: "Nativa (Inclusa)" },
              { label: "Entrega (Motoboy)", they: "Limitada/Difícil", us: "Nativa por Km/Fixo" }
            ].map((row, i) => (
              <div key={i} className="grid grid-cols-3 border-b border-surface-50 last:border-0 hover:bg-surface-50/30 transition-colors">
                <div className="p-6 md:p-8 font-bold text-sm text-surface-900 flex items-center border-r border-surface-50">{row.label}</div>
                <div className="p-6 md:p-8 text-center text-sm font-medium text-surface-400 flex items-center justify-center border-r border-surface-50">{row.they}</div>
                <div className="p-6 md:p-8 text-center text-sm font-black text-emerald-600 bg-brand-50/10 flex items-center justify-center gap-2">
                  <Check size={18} /> {row.us}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logistics Section */}
      <section className="py-24 bg-surface-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none opacity-10">
          <Smartphone size={600} className="text-brand-500 absolute -top-20 -right-40" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="flex-1 space-y-8">
              <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-[1.1]">
                Entregas sob medida: dos Correios ao seu Motoboy de confiança.
              </h2>
              <p className="text-xl text-brand-100 font-medium leading-relaxed">
                Ofereça a melhor experiência de entrega sem complicações técnicas. Automatizamos o cálculo e o rastreio para você.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-8">
                <div className="space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center">
                    <Layers size={24} className="text-brand-400" />
                  </div>
                  <h4 className="text-lg font-black">Melhor Envio</h4>
                  <p className="text-surface-400 text-sm font-medium">Integração nativa para etiquetas automáticas dos Correios e transportadoras.</p>
                </div>
                <div className="space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                    <Truck size={24} className="text-emerald-400" />
                  </div>
                  <h4 className="text-lg font-black">Motoboy Inteligente</h4>
                  <p className="text-surface-400 text-sm font-medium">Envio de nome do entregador e link de rastreio em tempo real via WhatsApp.</p>
                </div>
              </div>
            </div>

            <div className="flex-1 w-full max-w-md hidden lg:block">
              <div className="bg-white/5 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/10 space-y-6">
                <div className="flex items-center gap-4 border-b border-white/5 pb-6">
                  <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center font-bold text-white text-xl shadow-lg shadow-emerald-500/20">JS</div>
                  <div>
                    <p className="font-black text-sm uppercase tracking-widest text-white/40">Status do Pedido</p>
                    <p className="text-lg font-black">Saiu para Entrega!</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/40 font-bold uppercase tracking-widest text-[10px]">Entregador</span>
                    <span className="font-black">Ricardo Santos</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/40 font-bold uppercase tracking-widest text-[10px]">Veículo</span>
                    <span className="font-black">Moto (Honda CG 160)</span>
                  </div>
                </div>
                <button className="w-full bg-emerald-500 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors">
                  Rastrear no Mapa <Globe size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-surface-900">
              A tecnologia das grandes marcas, <br /> acessível para a sua loja.
            </h2>
            <p className="text-lg text-surface-500 font-medium">Sem fidelidade, cancele quando quiser.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">

            {/* Plan Básico */}
            <div className="bg-white p-10 rounded-[2.5rem] border border-surface-100 flex flex-col space-y-8 hover:shadow-2xl transition-all shadow-soft group">
              <div className="space-y-2">
                <h3 className="text-sm font-black text-surface-400 uppercase tracking-[0.2em]">Básico</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-surface-900 leading-none">R$</span>
                  <span className="text-5xl font-black text-surface-900 tracking-tighter leading-none">97</span>
                  <span className="text-surface-400 font-bold">/mês</span>
                </div>
              </div>
              <ul className="space-y-4 flex-1">
                {[
                  "Até 100 Produtos",
                  "IA Atendente Inclusa",
                  "Vitrine Digital Grátis",
                  "Melhor Envio + Motoboy"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-semibold text-surface-600">
                    <div className="w-5 h-5 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
                      <Check size={12} strokeWidth={4} />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => window.location.href = 'https://zapshop.app/register'}
                className="w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest border border-brand-100 text-brand-600 hover:bg-brand-50 transition-all active:scale-95 shadow-lg shadow-brand-900/5"
              >
                7 Dias Grátis - Assinar Básico
              </button>
            </div>

            {/* Plan Intermediário - DESTACADO */}
            <div className="bg-surface-900 p-10 rounded-[2.5rem] flex flex-col space-y-8 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.2)] transform lg:scale-110 relative z-20 overflow-hidden ring-4 ring-brand-500/20">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <Zap size={100} className="text-brand-400" />
              </div>
              <div className="space-y-2 relative z-10">
                <div className="inline-block bg-brand-500 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-2">Mais Popular</div>
                <h3 className="text-sm font-black text-brand-200 uppercase tracking-[0.2em]">Intermediário</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-white leading-none">R$</span>
                  <span className="text-5xl font-black text-white tracking-tighter leading-none">197</span>
                  <span className="text-brand-300 font-bold">/mês</span>
                </div>
              </div>
              <ul className="space-y-4 flex-1 relative z-10">
                {[
                  "Até 300 Produtos",
                  "Tudo do Plano Básico",
                  "Relatórios de Vendas",
                  "Domínio Personalizado",
                  "Suporte VIP"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-semibold text-brand-50">
                    <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center text-white shrink-0 shadow-lg shadow-brand-500/30">
                      <Check size={12} strokeWidth={4} />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => window.location.href = 'https://zapshop.app/register'}
                className="w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest bg-emerald-500 text-white hover:bg-emerald-600 transition-all active:scale-95 shadow-2xl shadow-emerald-500/20 relative z-10"
              >
                Experimentar 7 Dias Grátis
              </button>
            </div>

            {/* Plan Pro */}
            <div className="bg-white p-10 rounded-[2.5rem] border border-surface-100 flex flex-col space-y-8 hover:shadow-2xl transition-all shadow-soft group">
              <div className="space-y-2">
                <h3 className="text-sm font-black text-surface-400 uppercase tracking-[0.2em]">Pro</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-surface-900 leading-none">R$</span>
                  <span className="text-5xl font-black text-surface-900 tracking-tighter leading-none">497</span>
                  <span className="text-surface-400 font-bold">/mês</span>
                </div>
              </div>
              <ul className="space-y-4 flex-1">
                {[
                  "Produtos Ilimitados",
                  "Recursos Ilimitados",
                  "Tudo do Intermediário",
                  "Domínio Personalizado",
                  "Automações Avançadas"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-semibold text-surface-600">
                    <div className="w-5 h-5 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
                      <Check size={12} strokeWidth={4} />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => window.location.href = 'https://zapshop.app/register'}
                className="w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest border border-brand-100 text-brand-600 hover:bg-brand-50 transition-all active:scale-95 shadow-lg shadow-brand-900/5"
              >
                7 Dias Grátis - Assinar Pro
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-surface-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <HelpCircle size={48} className="text-brand-500 mx-auto" />
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-surface-900">Dúvidas Frequentes</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-[1.5rem] border border-surface-100 overflow-hidden shadow-sm transition-all hover:shadow-md">
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full p-6 text-left flex justify-between items-center outline-none group"
                >
                  <span className="font-bold text-surface-900 text-lg group-hover:text-brand-600 transition-colors">{f.q}</span>
                  {faqOpen === i ? <Minus size={20} className="text-brand-500" /> : <Plus size={20} className="text-surface-400" />}
                </button>
                <div className={`px-6 transition-all duration-300 ease-in-out bg-surface-50/30 overflow-hidden ${faqOpen === i ? 'pb-6 max-h-48 opacity-100 border-t border-surface-50 pt-4' : 'max-h-0 opacity-0'}`}>
                  <p className="text-surface-500 font-medium leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration CTA Bar */}
      <section className="bg-brand-600 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
          <h3 className="text-2xl md:text-3xl font-black text-white text-center md:text-left leading-tight">
            Pronto para transformar seu WhatsApp em uma <br className="hidden md:block" /> máquina de vendas totalmente automatizada?
          </h3>
          <button
            onClick={() => window.location.href = 'https://zapshop.app/register'}
            className="bg-white text-brand-700 font-black px-10 py-5 rounded-2xl shadow-2xl transition-all active:scale-95 hover:bg-brand-50 whitespace-nowrap text-sm uppercase tracking-widest"
          >
            Começar 7 Dias Grátis Agora
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-20 border-t border-surface-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2 space-y-8">
              <img src="/logozapshopsaas.png" alt="ZapShop" className="h-12 w-auto" />
              <p className="max-w-xs text-surface-500 font-medium leading-relaxed">
                A 1ª plataforma híbrida de venda conversacional do Brasil. IA + WhatsApp + Vitrine Digital em um só lugar.
              </p>
              <div className="flex items-center gap-4">
                <a href="https://www.instagram.com/vinicius_o_matos/" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-surface-50 flex items-center justify-center text-surface-400 hover:bg-brand-600 hover:text-white transition-all shadow-sm">
                  <Instagram size={18} />
                </a>
                <a href="mailto:contato@viniciusomatos.com" className="w-10 h-10 rounded-full bg-surface-50 flex items-center justify-center text-surface-400 hover:bg-brand-600 hover:text-white transition-all shadow-sm">
                  <Mail size={18} />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-surface-900 font-black uppercase tracking-widest text-[10px] mb-6">Plataforma</h4>
              <ul className="space-y-4 text-sm font-semibold text-surface-500">
                <li><a href="#solucao" className="hover:text-brand-600 transition-colors">Como funciona</a></li>
                <li><a href="#comparativo" className="hover:text-brand-600 transition-colors">Funcionalidades</a></li>
                <li><a href="#precos" className="hover:text-brand-600 transition-colors">Preços</a></li>
                <li><button onClick={() => navigate('/register')} className="hover:text-brand-600 transition-colors">Registro</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-surface-900 font-black uppercase tracking-widest text-[10px] mb-6">Suporte e Legal</h4>
              <ul className="space-y-4 text-sm font-semibold text-surface-500 text-left">
                <li><a href="https://wa.me/5521991000120?text=Preciso%20de%20ajuda%20com%20a%20plataforma%20Zapshop" target="_blank" rel="noreferrer" className="hover:text-brand-600 transition-colors">Ajuda</a></li>
                <li><button onClick={() => setShowTerms(true)} className="hover:text-brand-600 transition-colors text-left">Termos de Uso</button></li>
                <li><button onClick={() => setShowPrivacy(true)} className="hover:text-brand-600 transition-colors text-left">Privacidade</button></li>
                <li><a href="https://wa.me/5521991000120" target="_blank" rel="noreferrer" className="hover:text-brand-600 transition-colors">Contato</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-surface-50 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-xs text-surface-400 font-bold">
              © 2026 ZapShop. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-4 text-xs font-black uppercase tracking-widest text-surface-400">
              <span>Produto da família <a href="https://viniciusomatos.com/" target="_blank" rel="noreferrer" className="text-brand-600 font-bold no-underline hover:underline">Vinícius O Matos</a></span>
            </div>
          </div>
        </div>
      </footer>

      {/* Termos de Uso (View Only) */}
      {showTerms && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-scale-in">
            <div className="p-6 border-b border-surface-100 flex justify-between items-center bg-surface-50">
              <h2 className="text-xl font-black text-surface-900 uppercase tracking-tighter">Termos de Uso</h2>
              <button onClick={() => setShowTerms(false)} className="p-2 hover:bg-surface-200 rounded-full transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="p-8 overflow-y-auto prose prose-sm max-w-none prose-headings:text-surface-900 prose-p:text-surface-600 text-left">
              <p className="text-xs text-surface-400 mb-6 uppercase font-bold tracking-widest">Última atualização: Março de 2026</p>
              <h3 className="font-black">1. Aceitação dos Termos</h3>
              <p>Ao utilizar a plataforma ZapShop, você concorda em cumprir estes termos e todas as leis vigentes.</p>
              <h3 className="font-black">2. A Plataforma</h3>
              <p>O ZapShop é um sistema SaaS que automatiza vendas via WhatsApp usando IA e fornece vitrines digitais.</p>
              <h3 className="font-black">3. Responsabilidades do Lojista</h3>
              <p>O lojista é inteiramente responsável pelos produtos vendidos, atendimento ao cliente e cumprimento das obrigações fiscais.</p>
              <h3 className="font-black">4. Planos e Pagamentos</h3>
              <p>O acesso às ferramentas premium é liberado mediante confirmação de pagamento do plano escolhido.</p>
            </div>
            <div className="p-6 bg-surface-50 border-t border-surface-100 text-center">
              <button onClick={() => setShowTerms(false)} className="bg-surface-900 text-white px-8 py-3 rounded-xl font-bold text-sm uppercase tracking-widest hover:bg-black transition-all">Fechar Visualização</button>
            </div>
          </div>
        </div>
      )}

      {/* Política de Privacidade (LGPD) */}
      {showPrivacy && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-scale-in">
            <div className="p-6 border-b border-surface-100 flex justify-between items-center bg-surface-50">
              <h2 className="text-xl font-black text-surface-900 uppercase tracking-tighter">Política de Privacidade</h2>
              <button onClick={() => setShowPrivacy(false)} className="p-2 hover:bg-surface-200 rounded-full transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="p-8 overflow-y-auto prose prose-sm max-w-none text-left">
              <div className="flex items-center gap-3 mb-6 p-4 bg-emerald-50 rounded-2xl border border-emerald-100 italic text-emerald-800 text-xs font-semibold">
                <ShieldCheck size={20} /> Em conformidade com a LGPD (Lei Geral de Proteção de Dados) do Brasil.
              </div>
              <h3 className="font-black text-surface-900">1. Coleta e Uso de Dados</h3>
              <p className="text-surface-600">Coletamos informações essenciais para o funcionamento do seu e-commerce no WhatsApp, como número de celular, nome da loja e dados do catálogo.</p>
              <h3 className="font-black text-surface-900">2. Compartilhamento com Parceiros</h3>
              <p className="text-surface-600">Para garantir o pleno funcionamento da plataforma, poderemos compartilhar dados técnicos com parceiros estratégicos (ex: processadores de pagamento como Mercado Pago e serviços de logística como Melhor Envio). Esse compartilhamento é feito sob rigorosas camadas de segurança.</p>
              <h3 className="font-black text-surface-900">3. Preservação de Informações</h3>
              <p className="text-surface-600">Preservamos a segurança e integridade das informações coletadas em servidores criptografados, garantindo que os dados dos seus clientes pertençam apenas à sua operação.</p>
            </div>
            <div className="p-6 bg-surface-50 border-t border-surface-100 text-center">
              <button onClick={() => setShowPrivacy(false)} className="bg-surface-900 text-white px-8 py-3 rounded-xl font-bold text-sm uppercase tracking-widest hover:bg-black transition-all">Entendido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
